import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MultiSelectComboBox,
  Select,
  SettingsSection,
  Text,
  useToast,
  type MultiSelectOption,
  type SelectOption,
} from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useWorkflowDetail } from '../../../hooks/useWorkflows';
import { useTestSuites } from '../../../features/test-suites';
import { useEnvironmentStages } from '../../../features/properties/hooks/useEnvironmentStages';
import { extractUuid } from '../../../lib/prefixed-ids';

const DEPLOY_PREFIX = 'deploy:';
const NEVER = 'never';

type PromotionInput = { promoteOn: string; targetSuites: string[] };

function useSavePromotion(workflowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PromotionInput) => {
      const res = await fetch(`${API_URL}/workflows/${encodeURIComponent(workflowId)}/promotion`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to save promotion settings');
    },
    // Returned so `isPending` stays true until the fresh detail has landed.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] }),
  });
}

/** When a proven flow is promoted, and which suites it joins besides the default. */
export function PromotionTargetSettings({ workflowId }: { workflowId: string }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const enabled = user?.features?.sandboxV2 === true;
  const detail = useWorkflowDetail(enabled ? workflowId : null);
  const stagesQuery = useEnvironmentStages(enabled);
  const suitesQuery = useTestSuites(enabled);
  const save = useSavePromotion(workflowId);

  // Promotion needs the SANDBOX_V2 org flag; the API refuses it otherwise.
  if (!enabled) return null;

  const loadError = detail.error ?? stagesQuery.error ?? suitesQuery.error;
  const isLoading = detail.isLoading || stagesQuery.isLoading || suitesQuery.isLoading;
  const stored = detail.data;
  // Never show defaults for a setting we could not read: a change would overwrite the real one.
  const isReady = !!stored && !!stagesQuery.data && !!suitesQuery.data && !loadError;
  const promoteOn = stored?.promoteOn ?? '';
  const targetIds = stored?.targetSuites ?? [];

  const stageOptions: SelectOption[] = (stagesQuery.data?.stages ?? []).map((stage) => ({
    id: `${DEPLOY_PREFIX}${stage.name}`,
    label: `Deploy to ${stage.name}`,
  }));
  // A stored stage that is no longer in the org's list still shows as selected.
  if (promoteOn.startsWith(DEPLOY_PREFIX) && !stageOptions.some((o) => o.id === promoteOn)) {
    stageOptions.push({ id: promoteOn, label: `Deploy to ${promoteOn.slice(DEPLOY_PREFIX.length)}` });
  }
  const promoteOptions: SelectOption[] = [
    { id: 'merge', label: 'Merge' },
    { id: NEVER, label: 'Never (archive at merge)' },
    ...stageOptions,
  ];
  const isNever = promoteOn === NEVER;

  // Suite ids on both sides are raw UUIDs; extractUuid also accepts prefixed ids.
  const suiteOptions: MultiSelectOption[] = (suitesQuery.data ?? [])
    .filter((suite) => suite.kind === 'native')
    .map((suite) => ({ id: suite.id, label: suite.name }));
  const selectedSuites = suiteOptions.filter((option) =>
    targetIds.some((id) => extractUuid(id) === option.id)
  );

  const submit = async (input: PromotionInput) => {
    try {
      await save.mutateAsync(input);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save promotion settings');
    }
  };

  return (
    <SettingsSection
      title="Promotion"
      description="When this flow joins your regression suites once its pull request is proven, or never, to archive it at merge."
    >
      {loadError && (
        <Text size="sm" className="text-destructive">
          {loadError instanceof Error ? loadError.message : 'Failed to load promotion settings'}
        </Text>
      )}
      <Select
        label="Promote on"
        items={promoteOptions}
        selectedKey={isReady ? promoteOn : null}
        isDisabled={!isReady || save.isPending}
        onSelectionChange={(key) =>
          // Never joins no suite, so the targets are cleared in the same request.
          void submit({
            promoteOn: String(key),
            targetSuites: String(key) === NEVER ? [] : targetIds,
          })
        }
      />
      <MultiSelectComboBox<MultiSelectOption>
        label="Target suites"
        description={
          isNever
            ? 'This flow is archived at merge and joins no suite.'
            : 'Leave empty to add this flow to the default suite only.'
        }
        placeholder="Search suites…"
        items={suiteOptions}
        value={selectedSuites}
        isLoading={isLoading}
        isDisabled={!isReady || save.isPending || isNever}
        onChange={(items) =>
          void submit({ promoteOn, targetSuites: items.map((item) => item.id) })
        }
      />
    </SettingsSection>
  );
}
